import React, { useState, useEffect } from 'react';
import { pdfAPI } from '../../services/api';

/**
 * Download PDF by capturing iframe HTML and sending to server-side Playwright.
 * Uses headless Chromium — produces the exact same output as browser "Save as PDF".
 */
export const pdfBlobFromIframe = async (iframeSelector, orientation, filename, options = {}) => {
  const iframe = document.querySelector(iframeSelector);
  if (!iframe?.contentDocument) {
    throw new Error('Preview iframe not ready');
  }

  // Convert all images to inline base64 data URIs so Playwright can render them
  const images = iframe.contentDocument.querySelectorAll('img');
  for (const img of images) {
    if (img.complete && img.naturalWidth > 0 && !img.src.startsWith('data:')) {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext('2d').drawImage(img, 0, 0);
        img.src = canvas.toDataURL('image/png');
      } catch (e) { /* CORS — skip */ }
    }
  }

  let html = iframe.contentDocument.documentElement.outerHTML;
  html = html.replace(/<a[^>]*id="emergent-badge"[^>]*>[\s\S]*?<\/a>/gi, '');
  const response = await pdfAPI.generate(html, orientation, filename, options);
  return new Blob([response.data], { type: 'application/pdf' });
};

export const downloadPdfFromIframe = async (iframeSelector, orientation, filename, onStart, onComplete, options = {}) => {
  try {
    onStart?.();
    const blob = await pdfBlobFromIframe(iframeSelector, orientation, filename, options);
    // Trigger browser file download
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    onComplete?.(true);
  } catch (error) {
    console.error('PDF download failed:', error);
    onComplete?.(false);
    throw error;
  }
};

// html2pdf.js CDN for native PDF generation
const HTML2PDF_CDN = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';

// Load html2pdf script dynamically
export const loadHtml2Pdf = () => {
  return new Promise((resolve, reject) => {
    if (window.html2pdf) {
      resolve(window.html2pdf);
      return;
    }
    const existingScript = document.querySelector(`script[src="${HTML2PDF_CDN}"]`);
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(window.html2pdf));
      return;
    }
    const script = document.createElement('script');
    script.src = HTML2PDF_CDN;
    script.onload = () => resolve(window.html2pdf);
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

/**
 * Generate native A4 PDF from a container element
 */
export const generateNativePdf = async (element, filename, orientation = 'landscape', onStart, onComplete) => {
  try {
    onStart?.();
    await loadHtml2Pdf();
    const windowWidth = orientation === 'portrait' ? 760 : 1060;
    const options = {
      margin: [10, 14, 10, 14],
      filename: filename,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { 
        scale: 2, useCORS: true, allowTaint: true, logging: false,
        backgroundColor: '#ffffff', windowWidth: windowWidth,
        onclone: (clonedDoc, clonedEl) => {
          clonedDoc.querySelectorAll('.no-print, .download-pdf-btn, [data-no-print]').forEach(el => {
            el.style.display = 'none';
          });
          clonedEl.style.width = windowWidth + 'px';
          clonedEl.style.maxWidth = windowWidth + 'px';
          clonedEl.style.overflow = 'visible';
          clonedEl.style.boxSizing = 'border-box';
          clonedEl.style.padding = '10px';
          clonedEl.querySelectorAll('table').forEach(t => {
            t.style.width = '100%';
            t.style.maxWidth = '100%';
            t.style.tableLayout = 'fixed';
            t.style.overflow = 'visible';
          });
        },
      },
      jsPDF: { unit: 'mm', format: 'a4', orientation: orientation },
      pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
    };
    await window.html2pdf().set(options).from(element).save();
    onComplete?.();
  } catch (error) {
    onComplete?.();
    throw error;
  }
};

/**
 * PDF Preview Modal Component
 */
export const PdfPreviewModal = ({ 
  isOpen, onClose, onDownload, documentTitle, documentNumber,
  orientation = 'landscape', sourceElement, filename
}) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen || !sourceElement) {
      setBlobUrl(null);
      setError(null);
      return;
    }
    let cancelled = false;
    const generatePreview = async () => {
      setIsLoading(true);
      setError(null);
      try {
        await loadHtml2Pdf();
        const windowWidth = orientation === 'portrait' ? 760 : 1060;
        const options = {
          margin: [10, 14, 10, 14],
          filename: filename || 'preview.pdf',
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { 
            scale: 2, useCORS: true, allowTaint: true, logging: false,
            backgroundColor: '#ffffff', windowWidth: windowWidth,
            onclone: (clonedDoc, clonedEl) => {
              clonedDoc.querySelectorAll('.no-print, .download-pdf-btn, [data-no-print]').forEach(el => {
                el.style.display = 'none';
              });
              clonedEl.style.width = windowWidth + 'px';
              clonedEl.style.maxWidth = windowWidth + 'px';
              clonedEl.style.overflow = 'visible';
              clonedEl.style.boxSizing = 'border-box';
              clonedEl.style.padding = '10px';
              clonedEl.querySelectorAll('table').forEach(t => {
                t.style.width = '100%';
                t.style.maxWidth = '100%';
                t.style.tableLayout = 'fixed';
                t.style.overflow = 'visible';
              });
            },
          },
          jsPDF: { unit: 'mm', format: 'a4', orientation: orientation },
          pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
        };
        const blobUrlResult = await window.html2pdf()
          .set(options).from(sourceElement).output('bloburl');
        if (!cancelled) setBlobUrl(blobUrlResult);
      } catch (err) {
        if (!cancelled) setError('Failed to generate preview. Please try again.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    generatePreview();
    return () => { cancelled = true; };
  }, [isOpen, sourceElement, orientation, filename]);

  if (!isOpen) return null;

  const handleDownload = async () => {
    if (onDownload) await onDownload();
    onClose();
  };
  
  return (
    <div 
      className="pdf-preview-modal-overlay"
      onClick={(e) => e.target === e.currentTarget && !isLoading && onClose()}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)', zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
      }}
    >
      <div style={{
        background: '#ffffff', borderRadius: '12px',
        maxWidth: '95vw', maxHeight: '95vh',
        width: orientation === 'landscape' ? '1100px' : '700px',
        height: '90vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4)'
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 24px', borderBottom: '1px solid #e2e8f0',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: '#f8fafc', borderRadius: '12px 12px 0 0', flexShrink: 0
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#1e293b' }}>PDF Preview</h3>
            <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#64748b' }}>
              {documentTitle} - {documentNumber}
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <span style={{ 
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              background: '#e0e7ff', color: '#3730a3', padding: '6px 12px',
              borderRadius: '6px', fontSize: '13px', fontWeight: '500'
            }}>
              A4 {orientation.charAt(0).toUpperCase() + orientation.slice(1)}
            </span>
            <button onClick={onClose} disabled={isLoading}
              style={{ background: 'none', border: 'none', fontSize: '24px',
                cursor: isLoading ? 'not-allowed' : 'pointer', color: '#64748b',
                padding: '4px 8px', borderRadius: '4px', lineHeight: 1 }}>
              &times;
            </button>
          </div>
        </div>

        {/* Content */}
        <div style={{
          flex: 1, overflow: 'hidden', background: '#374151',
          display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative'
        }}>
          {isLoading && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', color: '#ffffff' }}>
              <div style={{ width: '48px', height: '48px', border: '3px solid rgba(255,255,255,0.3)',
                borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
              <span style={{ fontSize: '16px', fontWeight: '500' }}>Generating preview...</span>
            </div>
          )}
          {error && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', color: '#fecaca', textAlign: 'center', padding: '20px' }}>
              <span style={{ fontSize: '16px', fontWeight: '500' }}>{error}</span>
              <button onClick={onClose} style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #fecaca', background: 'transparent', color: '#fecaca', fontSize: '14px', cursor: 'pointer' }}>
                Close
              </button>
            </div>
          )}
          {blobUrl && !isLoading && !error && (
            <iframe src={blobUrl} title="PDF Preview" style={{ width: '100%', height: '100%', border: 'none', background: '#ffffff' }} />
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px', borderTop: '1px solid #e2e8f0',
          display: 'flex', justifyContent: 'flex-end', alignItems: 'center',
          background: '#f8fafc', borderRadius: '0 0 12px 12px', gap: '12px', flexShrink: 0
        }}>
          <button onClick={onClose} disabled={isLoading}
            style={{ padding: '10px 20px', borderRadius: '6px', border: '1px solid #e2e8f0', background: 'white', color: '#475569', fontSize: '14px', fontWeight: '500', cursor: isLoading ? 'not-allowed' : 'pointer' }}>
            Cancel
          </button>
          <button onClick={handleDownload} disabled={isLoading || !blobUrl}
            style={{ padding: '10px 24px', borderRadius: '6px', border: 'none', background: (isLoading || !blobUrl) ? '#94a3b8' : '#1e40af', color: 'white', fontSize: '14px', fontWeight: '600', cursor: (isLoading || !blobUrl) ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            Download PDF
          </button>
        </div>
      </div>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

// Print styles - clean A4 layout with professional letterhead
export const printStyles = `
  @media print {
    @page { size: A4; margin: 10mm; }
    body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    .no-print, .download-pdf-btn { display: none !important; }
    /* --- Shared pagination hygiene (added iter 82) ------------------------
       Keeps atomic blocks whole and forbids orphaned single lines across
       every print view. Individual print views can still add their own
       @media print rules (e.g. Order's 34mm reserved footer for the fixed
       signature panel); those layer on top of these defaults. */
    .items-table tr,
    .items-table thead,
    .totals-section,
    .totals-box,
    .terms-section h4,
    .footer { page-break-inside: avoid; }
    .terms-section p {
      page-break-inside: avoid;
      orphans: 3;
      widows: 3;
    }
    .items-table thead { display: table-header-group; }
    /* Row-group (not footer-group) so the TOTAL row prints once at the end
       instead of repeating on every page of a multi-page item table. */
    .items-table tfoot { display: table-row-group; }
  }
  @media print { .print-container.landscape { max-width: 297mm; } }
  @page :landscape { size: A4 landscape; }
  .print-container { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; max-width: 210mm; margin: 0 auto; padding: 15px; background: white; color: #333; }
  .print-container.landscape { max-width: 297mm; }
  .download-pdf-btn { position: fixed; top: 20px; right: 20px; z-index: 1000; background: #1e40af; color: white; border: none; padding: 12px 24px; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 8px; box-shadow: 0 4px 12px rgba(30,64,175,0.3); transition: all 0.2s ease; }
  .download-pdf-btn:hover { background: #1e3a8a; transform: translateY(-2px); box-shadow: 0 6px 16px rgba(30,64,175,0.4); }
  .download-pdf-btn:disabled { background: #94a3b8; cursor: not-allowed; transform: none; box-shadow: none; }
  .download-pdf-btn svg { width: 18px; height: 18px; }
  .letterhead { border-bottom: 3px solid #1e40af; padding-bottom: 10px; margin-bottom: 15px; }
  .letterhead-top { display: flex; align-items: center; gap: 15px; margin-bottom: 6px; }
  .letterhead-logo { width: 60px; height: 60px; object-fit: contain; }
  .letterhead-company { flex: 1; }
  .letterhead-company-name { font-size: 22px; font-weight: bold; color: #1e40af; margin: 0; letter-spacing: 0.5px; }
  .letterhead-address { font-size: 11px; color: #475569; margin: 3px 0 0 0; line-height: 1.4; }
  .letterhead-contact { display: flex; flex-wrap: wrap; gap: 12px; font-size: 10px; color: #475569; margin-top: 4px; }
  .letterhead-contact span { display: inline-flex; align-items: center; gap: 3px; }
  .letterhead-certifications { font-size: 9px; color: #059669; font-weight: 500; margin-top: 4px; padding: 3px 0; border-top: 1px solid #e2e8f0; }
  .letterhead-document { text-align: right; min-width: 140px; }
  .letterhead-document h2 { font-size: 18px; font-weight: bold; color: #1e293b; margin: 0; text-transform: uppercase; }
  .letterhead-document .doc-number { font-size: 13px; color: #1e40af; font-weight: 600; margin-top: 2px; }
  .letterhead-document .doc-revision { font-size: 11px; color: #7c3aed; font-weight: 600; }
  .print-header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #1e40af; padding-bottom: 15px; margin-bottom: 20px; }
  .company-name { font-size: 24px; font-weight: bold; color: #1e40af; margin: 0; }
  .company-subtitle { font-size: 12px; color: #666; margin-top: 4px; }
  .document-title { text-align: right; }
  .document-title h2 { font-size: 20px; font-weight: bold; color: #333; margin: 0; }
  .document-number { font-size: 14px; color: #1e40af; font-weight: 600; }
  .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px; }
  .info-box { background: #f8fafc; padding: 12px; border-radius: 6px; border: 1px solid #e2e8f0; }
  .info-box h4 { font-size: 11px; text-transform: uppercase; color: #64748b; margin: 0 0 8px 0; letter-spacing: 0.5px; }
  .info-box p { margin: 4px 0; font-size: 13px; }
  .info-box .value { font-weight: 600; color: #1e293b; }
  .items-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; font-size: 12px; }
  .items-table th { background: #1e40af; color: white; padding: 10px 8px; text-align: left; font-weight: 600; font-size: 11px; text-transform: uppercase; }
  .items-table td { padding: 10px 8px; border-bottom: 1px solid #e2e8f0; }
  .items-table tr:nth-child(even) { background: #f8fafc; }
  .items-table .number { text-align: right; font-family: 'Consolas', monospace; }
  .totals-section { display: flex; justify-content: flex-end; margin-bottom: 20px; }
  .totals-box { width: 300px; background: #f1f5f9; padding: 15px; border-radius: 6px; }
  .totals-row { display: flex; justify-content: space-between; padding: 6px 0; font-size: 13px; }
  .totals-row.total { border-top: 2px solid #1e40af; margin-top: 8px; padding-top: 10px; font-weight: bold; font-size: 15px; }
  .terms-section { background: #fefce8; padding: 15px; border-radius: 6px; border: 1px solid #fef08a; margin-bottom: 20px; }
  .terms-section h4 { font-size: 12px; font-weight: 600; color: #854d0e; margin: 0 0 8px 0; }
  .terms-section p { font-size: 11px; color: #713f12; margin: 4px 0; line-height: 1.5; }
  .footer { text-align: center; padding-top: 20px; border-top: 1px solid #e2e8f0; font-size: 10px; color: #94a3b8; }
  .confidential-banner { background: #dc2626; color: white; text-align: center; padding: 8px; font-weight: bold; font-size: 12px; margin-bottom: 20px; border-radius: 4px; }
  .process-step { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 12px; margin-bottom: 10px; }
  .process-step-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
  .step-number { background: #1e40af; color: white; width: 24px; height: 24px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: bold; }
  .step-name { font-weight: 600; font-size: 14px; }
  .step-badge { font-size: 10px; padding: 2px 8px; border-radius: 12px; text-transform: uppercase; }
  .badge-inhouse { background: #dbeafe; color: #1e40af; }
  .badge-outsourced { background: #fed7aa; color: #c2410c; }
  .step-details { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; font-size: 11px; }
  .step-detail { background: white; padding: 6px 8px; border-radius: 4px; }
  .step-detail label { color: #64748b; display: block; font-size: 10px; }
  .step-detail span { font-weight: 600; color: #1e293b; }
  .loading-container { display: flex; justify-content: center; align-items: center; min-height: 100vh; background: white; }
  .spinner { width: 40px; height: 40px; border: 3px solid #e2e8f0; border-top-color: #1e40af; border-radius: 50%; animation: spin 1s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }
`;

// Reusable Company Letterhead Component
export const CompanyLetterhead = ({ documentType, documentNumber, revisionNumber }) => (
  <div className="letterhead">
    <div className="letterhead-top">
      <img src="/logo.png" alt="Company Logo" className="letterhead-logo"
        onError={(e) => { e.target.style.display = 'none'; }} />
      <div className="letterhead-company">
        <h1 className="letterhead-company-name">SINGHANIA INTERNATIONAL LIMITED</h1>
        <p className="letterhead-address">
          C-25, Phase-1, Focal Point, Dhandari Kalan, Ludhiana - 141010, Punjab, India
        </p>
        <div className="letterhead-contact">
          <span>Email: info@singhaniainternational.com, anshul@singhaniainternational.com</span>
          <span>Phone: +91 161 4611418, +91 9357681418</span>
        </div>
        <div className="letterhead-contact">
          <span>Web: www.sturdfix.com | www.singhaniainternational.com</span>
        </div>
        <p className="letterhead-certifications">
          An ISO 9001, ISO 14001, ISO 45001 and IATF 16949 certified company
        </p>
      </div>
      <div className="letterhead-document">
        <h2>{documentType}</h2>
        <p className="doc-number">{documentNumber}</p>
        {revisionNumber > 0 && (
          <p className="doc-revision">(Revision {revisionNumber})</p>
        )}
      </div>
    </div>
  </div>
);
